/**
 * Isometric floor tile pixel data — with per-type textures.
 *
 * Each tile is a 64x32 isometric diamond.
 * Palette indices reference TILE_PALETTE from palette.ts.
 *
 * Texture types:
 *   "carpet"   — noise dithering (office)
 *   "wood"     — horizontal grain lines (manager)
 *   "concrete" — aggregate speckle (factory)
 *   "linoleum" — micro-grid (break room)
 *   "plank"    — wide plank lines (stage)
 */

import type { SpriteDefinition } from "../SpriteSheet.js";

const W = 64;
const H = 32;

type TileTexture = "carpet" | "wood" | "concrete" | "linoleum" | "plank" | "grass" | "stone";

/**
 * Generate an isometric diamond with interior texture.
 *
 * `fill` and `fillAlt` are mixed in the interior according to `texture`.
 * `edgeLight` / `edgeDark` form the 2-pixel border.
 * `grainOffset` shifts grain/plank lines for the alt tile variant.
 */
function makeTexturedDiamond(
  fill: number,
  fillAlt: number,
  edgeLight: number,
  edgeDark: number,
  texture: TileTexture,
  grainOffset = 0,
): number[] {
  const pixels = new Array<number>(W * H).fill(0);

  for (let row = 0; row < H; row++) {
    // 2:1 isometric ratio: diamond fills full 64px width for seamless tessellation
    const ratio = W / H; // = 2
    let halfWidth: number;
    if (row < H / 2) {
      halfWidth = (row + 1) * ratio;
    } else {
      halfWidth = (H - row) * ratio;
    }

    const centerX = W / 2;
    const startX = centerX - halfWidth;
    const endX = centerX + halfWidth;

    for (let x = startX; x < endX; x++) {
      const off = row * W + x;

      // 2-pixel edge border
      if (x === startX || x === startX + 1) {
        pixels[off] = edgeLight;
      } else if (x === endX - 1 || x === endX - 2) {
        pixels[off] = edgeDark;
      } else if (row === 0 || row === 1) {
        pixels[off] = edgeLight;
      } else if (row === H - 1 || row === H - 2) {
        pixels[off] = edgeDark;
      } else {
        // Interior — apply texture
        pixels[off] = texturePixel(x, row, fill, fillAlt, edgeLight, texture, grainOffset);
      }
    }
  }

  return pixels;
}

/** Determine which palette index to use for an interior pixel based on texture type. */
function texturePixel(
  x: number, y: number,
  fill: number, fillAlt: number, edgeLight: number,
  texture: TileTexture,
  grainOffset: number,
): number {
  switch (texture) {
    case "carpet": {
      // Noise dithering — ~20% of pixels use fillAlt for carpet weave feel
      const hash = (x * 7 + y * 13) % 5;
      return hash === 0 ? fillAlt : fill;
    }
    case "wood": {
      // Horizontal grain lines every 3 rows
      const adjustedRow = y + grainOffset;
      return adjustedRow % 3 === 0 ? fillAlt : fill;
    }
    case "concrete": {
      // Aggregate speckle — ~25% lighter pixels
      const hash = (x * 3 + y * 11) % 4;
      return hash === 0 ? edgeLight : fill;
    }
    case "linoleum": {
      // Micro-grid pattern — faint seam lines
      if (x % 6 === 0 || y % 4 === 0) return fillAlt;
      return fill;
    }
    case "plank": {
      // Wide plank lines every 5 rows
      const adjustedRow = y + grainOffset;
      return adjustedRow % 5 === 0 ? fillAlt : fill;
    }
    case "grass": {
      // Scattered grass blades — ~15% variation for natural look
      const hash = (x * 11 + y * 7) % 7;
      return hash === 0 ? fillAlt : fill;
    }
    case "stone": {
      // Cobblestone grid — subtle mortar lines
      if ((x + grainOffset) % 8 === 0 || (y + grainOffset) % 6 === 0) return fillAlt;
      return fill;
    }
  }
}

// ---- Tile exports ----

/** Standard office floor tile — carpet texture. */
export const TILE_OFFICE_FLOOR: SpriteDefinition = {
  name: "tile-office",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(1, 3, 4, 5, "carpet") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate office floor tile — carpet texture, reversed colors for checkerboard. */
export const TILE_OFFICE_FLOOR_ALT: SpriteDefinition = {
  name: "tile-office-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(2, 1, 4, 5, "carpet") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Factory concrete floor tile — speckle texture. */
export const TILE_FACTORY_FLOOR: SpriteDefinition = {
  name: "tile-factory",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(6, 7, 7, 8, "concrete") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Stage wood floor tile — wide plank lines. */
export const TILE_STAGE_FLOOR: SpriteDefinition = {
  name: "tile-stage",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(9, 10, 10, 11, "plank") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Manager floor tile — dark warm wood grain. */
export const TILE_MANAGER_FLOOR: SpriteDefinition = {
  name: "tile-manager",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(13, 14, 4, 5, "wood") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate manager floor tile — offset grain for visual variety. */
export const TILE_MANAGER_FLOOR_ALT: SpriteDefinition = {
  name: "tile-manager-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(14, 13, 4, 5, "wood", 1) },
  ],
  originX: W / 2,
  originY: 0,
};

/** Break room floor tile — linoleum micro-grid. */
export const TILE_BREAK_FLOOR: SpriteDefinition = {
  name: "tile-break",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(15, 16, 4, 5, "linoleum") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate break room floor tile — reversed linoleum colors. */
export const TILE_BREAK_FLOOR_ALT: SpriteDefinition = {
  name: "tile-break-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(16, 15, 4, 5, "linoleum") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Corridor floor tile — smooth neutral stone. */
export const TILE_CORRIDOR_FLOOR: SpriteDefinition = {
  name: "tile-corridor",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(17, 18, 4, 5, "linoleum") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate corridor floor tile — reversed for checkerboard. */
export const TILE_CORRIDOR_FLOOR_ALT: SpriteDefinition = {
  name: "tile-corridor-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(18, 17, 4, 5, "linoleum") },
  ],
  originX: W / 2,
  originY: 0,
};

// ---- Grass tiles ----

/** Grass ground tile — natural scattered texture. */
export const TILE_GRASS: SpriteDefinition = {
  name: "tile-grass",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(19, 20, 21, 22, "grass") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate grass tile — reversed greens for checkerboard. */
export const TILE_GRASS_ALT: SpriteDefinition = {
  name: "tile-grass-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(20, 19, 21, 22, "grass") },
  ],
  originX: W / 2,
  originY: 0,
};

// ---- Path tiles ----

/** Stone path tile — cobblestone texture. */
export const TILE_PATH: SpriteDefinition = {
  name: "tile-path",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(23, 24, 25, 26, "stone") },
  ],
  originX: W / 2,
  originY: 0,
};

/** Alternate path tile — offset stone pattern for checkerboard. */
export const TILE_PATH_ALT: SpriteDefinition = {
  name: "tile-path-alt",
  frames: [
    { width: W, height: H, pixels: makeTexturedDiamond(24, 23, 25, 26, "stone", 3) },
  ],
  originX: W / 2,
  originY: 0,
};

// ---- Shadow tile (unchanged) ----

/** Shadow tile for floor perimeter — darkened overlay to ground the room. */
function makeShadowDiamond(): number[] {
  const pixels = new Array<number>(W * H).fill(0);

  for (let row = 0; row < H; row++) {
    // 2:1 isometric ratio: diamond fills full 64px width for seamless tessellation
    const ratio = W / H; // = 2
    let halfWidth: number;
    if (row < H / 2) {
      halfWidth = (row + 1) * ratio;
    } else {
      halfWidth = (H - row) * ratio;
    }

    const centerX = W / 2;
    const startX = centerX - halfWidth;
    const endX = centerX + halfWidth;

    for (let x = startX; x < endX; x++) {
      pixels[row * W + x] = 12;
    }
  }

  return pixels;
}

/** Floor shadow tile — semi-transparent dark overlay for room perimeter. */
export const TILE_FLOOR_SHADOW: SpriteDefinition = {
  name: "tile-shadow",
  frames: [
    { width: W, height: H, pixels: makeShadowDiamond() },
  ],
  originX: W / 2,
  originY: 0,
};
